import * as React from 'react';
import styled from 'styled-components';
import {
  DateRangeTypeOption,
  exclusiveDateRangeProvider,
  IExclusiveDateOption,
  inclusiveDateRangeProvider,
} from '../../../../data/providers/settings/dates/dateRangeProvider';
import { IRange } from '../../../../data/types';
import { Column } from '../../../layout/Column';
import { ControlSpacer } from '../ControlSpacer';
import { Select } from '../Select';
import { TimeframeSelect } from './TimeframeSelect';

interface IProps {
  type: 'exclusive' | 'inclusive';
  label?: string;
  dateRangeType: DateRangeTypeOption;
  dateRange: IRange;
  disallowCustomRange?: boolean;
  disallowLastSession?: boolean;
  disallowAllTime?: boolean;
  disallowTwoComparison?: boolean;
  enforceRange?: boolean;
  limitToTwenty?: boolean;
  onChange: (type: DateRangeTypeOption, dateRange: IRange) => void;
  id?: string;
}

export const DateSelect = (props: IProps): React.ReactElement => {
  /**
   * @description handles change to date type
   * @param opt
   */
  const handleTypeChange = (
    opt: IExclusiveDateOption | IExclusiveDateOption
  ): void => {
    props.onChange(opt, props.dateRange ?? null);
  };

  /**
   * @description handle date range change
   * @param selectedDates
   */
  const handleDateChange = (range: IRange): void => {
    props.onChange(props.dateRangeType, range);
  };

  const dateRangeOptions = React.useMemo((): DateRangeTypeOption[] => {
    const {
      disallowCustomRange,
      disallowLastSession,
      type,
      enforceRange,
      limitToTwenty,
      disallowAllTime,
    } = props;
    if (type === 'inclusive') {
      return inclusiveDateRangeProvider({
        disallowAllTime,
        disallowCustomRange,
        disallowLastSession,
        enforceRange,
        limitToTwenty,
      });
    } else {
      return exclusiveDateRangeProvider({
        disallowAllTime,
        disallowCustomRange,
        disallowLastSession,
        enforceRange,
        limitToTwenty,
      });
    }
  }, [props.disallowCustomRange, props.disallowLastSession]);

  return (
    <Column>
      {props?.dateRangeType?.value !== 'two-date' && (
        <ControlSpacer>
          <ControlLabel>
            {props.label ? props.label : 'Date Range'}
          </ControlLabel>
          <Select
            value={props.dateRangeType}
            options={dateRangeOptions}
            onChange={handleTypeChange}
            menuPortalTarget={document.body}
            menuPlacement='auto'
            inputId={props.id ?? 'date-range-select'}
          />
        </ControlSpacer>
      )}
      {!props.disallowCustomRange &&
        (props?.dateRangeType?.value === 'custom' ||
          props?.dateRangeType?.value === 'two-date') && (
          <TimeframeSelect
            onChange={handleDateChange}
            dateRange={props.dateRange}
          />
        )}
    </Column>
  );
};

const ControlLabel = styled.div`
  padding-bottom: 5px;
`;
